import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRightIcon } from '@phosphor-icons/react';
import { Card, EntityAvatar, Tag } from '@/components';
import { cn } from '@/lib/utils';
import {
    DirectoryItemInfoPanel,
    type DirectoryItemInfoField,
} from '@/features/DirectoryItemPage';

interface EventEntityCardProps {
    icon: ReactNode;
    imageUrl?: string;
    name: string;
    badge?: ReactNode;
    tags?: string[];
    fields: DirectoryItemInfoField[];
    href?: string;
    returnUrl?: string;
    actions?: ReactNode;
}

export function EventEntityCard({
    icon,
    imageUrl,
    name,
    badge,
    tags,
    fields,
    href,
    returnUrl,
    actions,
}: EventEntityCardProps) {
    const baseCardClasses = 'relative overflow-hidden w-full rounded-lg';

    const linkedCardClasses = cn(
        baseCardClasses,
        'group block text-left no-underline text-inherit cursor-pointer',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white-100/30',
        'focus-visible:ring-offset-2 focus-visible:ring-offset-grey-900',
    );

    const showLinkArrow = Boolean(href) && !actions;
    const showActionsCorner = Boolean(actions);

    const cornerLink =
        href && actions ? (
            <Link
                to={href}
                state={returnUrl ? { returnUrl } : undefined}
                className={cn(
                    'absolute top-3 right-12 z-20 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md',
                    'text-grey-200 outline-none transition-colors hover:bg-grey-500 hover:text-white-100',
                )}
                aria-label="Open"
            >
                <ArrowUpRightIcon className="size-4" />
            </Link>
        ) : null;

    const cardContent = (
        <>
            <div className="absolute inset-0 conntour-bg" />
            <div className="absolute inset-0 bg-[linear-gradient(291deg,_#1B1C1E_58.12%,_transparent_108.77%)]" />

            <div className="relative">
                {cornerLink}
                {showActionsCorner ? (
                    <div className="absolute top-3 right-3 z-20">{actions}</div>
                ) : null}
                {showLinkArrow ? (
                    <span
                        className={cn(
                            'absolute top-4 right-4 z-10 inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-[16px]',
                            'text-grey-200 transition-colors',
                            'group-hover:text-grey-100',
                        )}
                        aria-hidden
                    >
                        <ArrowUpRightIcon className="size-4" />
                    </span>
                ) : null}

                <div
                    className={cn(
                        'p-5 flex items-center gap-3',
                        (showLinkArrow || showActionsCorner) && 'pr-14',
                        showActionsCorner && href && 'pr-24',
                    )}
                >
                    {href && actions ? (
                        <Link
                            to={href}
                            state={returnUrl ? { returnUrl } : undefined}
                            className="flex items-center gap-3 min-w-0 no-underline text-inherit outline-none"
                        >
                            <EntityAvatar icon={icon} imageUrl={imageUrl} />
                            <div className="flex flex-col gap-3 min-w-0">
                                <div className="flex items-center gap-2.5">
                                    <span className="text-lg-medium text-white-100 truncate">
                                        {name}
                                    </span>
                                    {badge}
                                </div>
                                {tags && tags.length > 0 && (
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {tags.map((tag, index) => (
                                            <Tag key={index}>{tag}</Tag>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </Link>
                    ) : (
                        <>
                            <EntityAvatar icon={icon} imageUrl={imageUrl} />
                            <div className="flex flex-col gap-3 min-w-0">
                                <div className="flex items-center gap-2.5">
                                    <span className="text-lg-medium text-white-100 truncate">
                                        {name}
                                    </span>
                                    {badge}
                                </div>
                                {tags && tags.length > 0 && (
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {tags.map((tag, index) => (
                                            <Tag key={index}>{tag}</Tag>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>

                <div className="pb-5">
                    <DirectoryItemInfoPanel fields={fields} />
                </div>
            </div>
        </>
    );

    const cardBody = (
        <Card
            className={cn(
                baseCardClasses,
                href && !actions && 'cursor-pointer',
            )}
        >
            {cardContent}
        </Card>
    );

    if (href && !actions) {
        return (
            <Link
                to={href}
                state={returnUrl ? { returnUrl } : undefined}
                className={linkedCardClasses}
            >
                {cardBody}
            </Link>
        );
    }

    return cardBody;
}
